<script setup lang="ts">
// Vue
import { onMounted, ref, watch, computed } from "vue";

// Utils
import { formatNumber } from "@/services/Utils/DecimalFormatter";

// Components

const props = withDefaults(
  defineProps<{
    label: (value: string) => string;
    subLabel?: (value: string) => string;
    value?: number;
    thresholds?: { value: number; color: string }[];
    disableAutoClose?: boolean;
    scrollableContainer?: string;
    showLoadingSpinner?: boolean;
    error?: boolean;
    loading?: boolean;
    forceOpen?: boolean;
  }>(),
  {
    value: 0,
    subLabel: () => "",
    thresholds: () => [
      { value: 0, color: "bg-slate-300" },
      { value: 25, color: "bg-app-red-300" },
      { value: 75, color: "bg-app-yellow-400" },
      { value: 100, color: "bg-app-green-400" },
    ],
    disableAutoClose: false,
    scrollableContainer: "scrollable",
    loading: false,
  }
);

const emit = defineEmits<{
  (e: "refetch"): void;
}>();

// ///////////////////
// Providers
// ///////////////////

// Listen to page scroll and toggle height when user passes current element
const handleScroll = (event: any) => {
  if (props.disableAutoClose) {
    return;
  }
  if (event.target.scrollTop < 50 && !closedByClick.value) {
    toggle(true);
    if (openedByClick.value) {
      openedByClick.value = false;
    }
  }
  if (event.target.scrollTop > 50 && !openedByClick.value) {
    toggle(false);
    if (closedByClick.value) {
      closedByClick.value = false;
    }
  }
};

const open = ref<boolean>(false);
const openedByClick = ref<boolean>(false);
const closedByClick = ref<boolean>(false);

const toggle = (force?: boolean) => {
  if (force !== undefined) {
    open.value = force;
  } else {
    open.value = !open.value;
  }
};

const toggleByClick = () => {
  if (open.value) {
    openedByClick.value = false;
    closedByClick.value = true;
  } else {
    closedByClick.value = false;
    openedByClick.value = true;
  }
  toggle();
};

onMounted(() => {
  const scrollable = document.getElementById(props.scrollableContainer);
  // Apply handlescroll to page scroll
  if (scrollable) {
    scrollable.addEventListener("scroll", handleScroll);
  }
});

const valueColor = computed(() => {
  const first = props.thresholds[0];
  const last = props.thresholds[props.thresholds.length - 1];
  const restOfThresholds = props.thresholds.slice(1, -1);

  if (props.value >= last.value) {
    return last.color;
  }

  if (props.value <= first.value) {
    return first.color;
  }

  if (props.value < restOfThresholds[0]?.value) {
    return restOfThresholds[0].color;
  }

  for (let i = 0; i < restOfThresholds.length; i++) {
    if (props.value < restOfThresholds[i + 1]?.value) {
      return restOfThresholds[i + 1].color;
    }
  }

  return last.color;
});

const colorOpacityMap: Record<string, { track: string; fill: string }> = {
  "bg-slate-300": { track: "bg-slate-300/20", fill: "bg-slate-300/65 group-hover:bg-slate-300" },
  "bg-app-red-300": {
    track: "bg-app-red-300/20 dark:bg-app-red-500/20",
    fill: "bg-app-red-300/65 group-hover:bg-app-red-300 dark:bg-app-red-500/80 dark:group-hover:bg-app-red-500",
  },
  "bg-app-yellow-400": {
    track: "bg-app-yellow-400/20 dark:bg-app-yellow-500/20",
    fill: "bg-app-yellow-400/65 group-hover:bg-app-yellow-400 dark:bg-app-yellow-500/80 dark:group-hover:bg-app-yellow-500",
  },
  "bg-app-green-400": {
    track: "bg-app-green-400/20 dark:bg-app-green-500/20",
    fill: "bg-app-green-400/65 group-hover:bg-app-green-400 dark:bg-app-green-500/80 dark:group-hover:bg-app-green-500",
  },
};

const trackClass = computed(() => {
  if (props.loading) return "cursor-wait bg-slate-300/20";
  return colorOpacityMap[valueColor.value]?.track ?? "bg-slate-300/20";
});

const fillClass = computed(() => {
  return colorOpacityMap[valueColor.value]?.fill ?? "bg-slate-300/65 group-hover:bg-slate-300";
});

watch(
  () => props.value,
  () => {
    setTimeout(() => {
      toggle(true);
    }, 300);
  },
  { immediate: true }
);
</script>

<template>
  <div
    ref="valueWrapper"
    class="group duration-normal ease-standard dark:text-type-default relative flex w-full cursor-pointer overflow-hidden rounded-[4px] text-slate-600 transition-all"
    :class="trackClass"
    @click="toggleByClick">
    <!-- style kept: width is a data-driven percentage value that cannot be expressed with Tailwind -->
    <div
      class="duration-normal ease-standard rounded-[4px] whitespace-nowrap transition-all"
      :class="[fillClass, forceOpen || (open && !loading) ? 'h-6' : 'h-[10px]']"
      :style="`width: ${value}%`">
      <!-- Loading shimmer overlay -->
      <div
        class="progress-shimmer duration-normal ease-standard absolute inset-0 rounded-[4px] transition-opacity"
        :class="[loading ? 'opacity-100' : 'opacity-0']" />
      <!-- Visible text -->
      <div
        class="t-footnote-bold absolute top-[50%] right-2 flex translate-y-[-50%] transform space-x-4 pt-px transition-opacity select-none"
        :class="[open && !loading ? 'opacity-100 duration-500' : 'duration-150']">
        <!-- Loading spinner -->
        <span
          v-if="loading && showLoadingSpinner"
          class="loader border-t-app-blue-500 z-10 h-3 w-3 animate-spin rounded-full border-2 border-slate-300" />

        <!-- Error state (same location as spinner) -->
        <template v-else-if="error">
          <span class="text-app-red-600 whitespace-nowrap"> Could not load emissions </span>

          <button
            type="button"
            class="bg-app-red-300/10 hover:bg-app-red-300/20 rounded-4 t-footnote-bold items-center gap-1 px-2"
            title="Retry"
            @mousedown.stop
            @click.stop.prevent="emit('refetch')">
            <span>Retry</span>
          </button>
        </template>

        <template v-else>
          <span>{{ label(formatNumber(value)) }}</span>
          <span v-if="subLabel(formatNumber(value))">{{ subLabel(formatNumber(value)) }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes Slide {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes skeleton-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* overlay container (already absolute inset-0 in markup) */
.progress-shimmer {
  overflow: hidden;
}

/* the moving highlight */
.progress-shimmer::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: skeleton-shimmer 1.4s infinite;
}
</style>
